import { useId, useState } from 'react';
import { SendOutlined } from '@ant-design/icons';

import { Badge, Button, Checkbox, Dialog, Field, Input, Select } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import { useToast } from '../../../../lib/toast';
import { LoiTrongHop } from '../../../tenant/components/phan-form';
import { DongBatTat } from '../chung';
import { KENH_CAU_HINH, QUYEN } from '../../constants';
import { useGuiThuKenh, useLuuQuyTac, useTaiKhoanGui } from '../../hooks/useThongBao';
import {
  coBoGui,
  formQuyTac,
  giuWorkflowTrongPhamVi,
  GIOI_HAN_QUY_TAC,
  KENH,
  kiemQuyTac,
  LOAI_TAI_KHOAN_CUA_KENH,
  SU_KIEN,
  taiKhoanCuaLoai,
  TEN_KENH,
  thanQuyTac,
} from '../../nghiep-vu/thong-bao';
import type { FormQuyTac, LoiQuyTac } from '../../nghiep-vu/thong-bao';
import type { KenhCauHinh, KenhThongBao, ProjectOpenRpa, QuyTacThongBao, TaiKhoanGui } from '../../types';
import { ChonWorkflow } from './ChonWorkflow';

/**
 * Thêm / sửa một cấu hình thông báo (`NotificationFormDialog` của native). Token, mật khẩu không nhập ở đây: lấy từ
 * tab Tài khoản gửi (Telegram, Gmail) hoặc biến môi trường của máy chủ (Zalo, WhatsApp).
 */
export function HopQuyTac({
  goc,
  projectMacDinh,
  projects,
  docProjectDuoc,
  kenh,
  onClose,
}: {
  goc: QuyTacThongBao | null;
  /** Đang xem một Project thì quy tắc mới thuộc Project đó. */
  projectMacDinh: string;
  projects: readonly ProjectOpenRpa[];
  docProjectDuoc: boolean;
  kenh: readonly KenhCauHinh[];
  onClose: () => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const formId = useId();
  const luu = useLuuQuyTac();
  const guiThu = useGuiThuKenh();
  const taiKhoan = useTaiKhoanGui();
  const [f, setF] = useState<FormQuyTac>(() => formQuyTac(goc, projectMacDinh));
  const [loi, setLoi] = useState<LoiQuyTac>({});
  const [dangThu, setDangThu] = useState<KenhThongBao | null>(null);

  const doi = (moi: Partial<FormQuyTac>) => {
    setF((cu) => ({ ...cu, ...moi }));
    luu.reset();
  };
  const batKenh = (k: KenhThongBao, bat: boolean) => {
    doi({ channels: KENH.filter((x) => (x === k ? bat : f.channels.includes(x))) });
    setLoi((cu) => ({ ...cu, channels: undefined, [k]: undefined }));
  };

  // Phạm vi đang lưu là Project không còn trên OpenCore: vẫn hiện để sửa hoặc đổi.
  const luaChonPhamVi = [
    { value: '', label: t('rpaThongBao.tatCaDuAn') },
    ...projects.map((p) => ({ value: p.id, label: p.name })),
    ...(f.projectCode && !projects.some((p) => p.id.toUpperCase() === f.projectCode.toUpperCase())
      ? [{ value: f.projectCode, label: f.projectCode }]
      : []),
  ];

  const thu = (k: KenhThongBao) => {
    const dich = (f.targets[k] ?? '').trim();
    const sai = kiemQuyTac({ ...f, channels: [k] })[k];
    if (sai) {
      setLoi((cu) => ({ ...cu, [k]: sai }));
      return;
    }
    setDangThu(k);
    guiThu.mutate(
      { channel: k, target: dich, accountId: f.senders[k] ?? '' },
      {
        onSuccess: () => toast.success(t('rpaThongBao.form.daGuiThu', { kenh: TEN_KENH[k] })),
        onError: (e) => toast.error(dichLoi(e)),
        onSettled: () => setDangThu(null),
      },
    );
  };

  const gui = () => {
    const tim = kiemQuyTac(f);
    setLoi(tim);
    if (Object.keys(tim).length > 0 || luu.isPending) return;
    luu.mutate(
      { id: goc?.id ?? null, than: thanQuyTac(f, goc) },
      {
        onSuccess: () => {
          toast.success(t(goc ? 'rpaThongBao.form.daLuu' : 'rpaThongBao.form.daTao'));
          onClose();
        },
      },
    );
  };

  const loiKenh = (k: KenhThongBao) => {
    const l = loi[k];
    if (l === 'batBuoc') return t('rpaThongBao.form.batBuoc');
    if (l === 'saiDinhDang') return t(`rpaThongBao.saiDinhDang.${k}`);
    return undefined;
  };

  return (
    <Dialog
      open
      onClose={onClose}
      width={720}
      destroyOnHidden
      title={goc ? t('rpaThongBao.form.suaTieuDe', { ten: goc.name }) : t('rpaThongBao.form.taoTieuDe')}
      footer={
        <div className="space-y-3">
          {luu.error && <LoiTrongHop>{dichLoi(luu.error)}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={luu.isPending}>
              {luu.isPending ? t('chung.dangLuu') : t(goc ? 'rpaThongBao.form.luu' : 'rpaThongBao.form.them')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-5 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          gui();
        }}
      >
        <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
          <Field label={t('rpaThongBao.form.ten')} required error={loi.name ? t('rpaThongBao.form.batBuoc') : undefined}>
            {(p) => (
              <Input
                {...p}
                autoFocus
                autoComplete="off"
                maxLength={GIOI_HAN_QUY_TAC.ten}
                placeholder={t('rpaThongBao.form.tenMau')}
                value={f.name}
                onChange={(e) => {
                  doi({ name: e.target.value });
                  setLoi((cu) => ({ ...cu, name: undefined }));
                }}
              />
            )}
          </Field>
          <Field label={t('rpaThongBao.form.suKien')}>
            {(p) => (
              <Select<string>
                id={p.id}
                className="w-full"
                value={f.event}
                onChange={(v) => doi({ event: v as FormQuyTac['event'] })}
                options={SU_KIEN.map((e) => ({
                  value: e,
                  label: coBoGui(e)
                    ? t(`rpaThongBao.suKien.${e}`)
                    : t('rpaThongBao.form.chuaCoBoGui', { ten: t(`rpaThongBao.suKien.${e}`) }),
                }))}
              />
            )}
          </Field>
          <Field
            label={t('rpaThongBao.form.phamVi')}
            hint={docProjectDuoc ? undefined : t('rpaThongBao.form.thieuQuyenProject', { quyen: QUYEN.projectDoc })}
          >
            {(p) => (
              <Select<string>
                id={p.id}
                aria-describedby={p['aria-describedby']}
                className="w-full"
                showSearch
                optionFilterProp="label"
                value={f.projectCode}
                onChange={(v) => doi({ projectCode: v, workflows: giuWorkflowTrongPhamVi(f.workflows, projects, v) })}
                options={luaChonPhamVi}
              />
            )}
          </Field>
          <Field label={t('rpaThongBao.form.workflow')} hint={t('rpaThongBao.form.workflowGoiY')}>
            {(p) => (
              <ChonWorkflow
                id={p.id}
                describedBy={p['aria-describedby']}
                projects={projects}
                projectId={f.projectCode}
                value={f.workflows}
                onChange={(workflows) => doi({ workflows })}
              />
            )}
          </Field>
        </div>

        <fieldset className="space-y-2">
          <legend className="mb-2 text-sm font-semibold text-content-primary">{t('rpaThongBao.form.kenh')}</legend>
          {loi.channels && (
            <p className="text-xs text-danger-700" role="alert">
              {t('rpaThongBao.form.kenhBatBuoc')}
            </p>
          )}
          {KENH.map((k) => {
            const bat = f.channels.includes(k);
            const tt = kenh.find((x) => x.type === k);
            const ch = tt ? KENH_CAU_HINH[tt.configured ? 'co' : 'chua'] : null;
            return (
              <div key={k} className={bat ? 'rounded-lg border border-primary-200 p-3' : 'rounded-lg border border-default px-3 py-2.5'}>
                <div className="flex flex-wrap items-center gap-2">
                  <Checkbox checked={bat} onChange={(e) => batKenh(k, e.target.checked)} className="min-w-0 flex-1">
                    <span className="text-sm font-medium text-content-primary">{TEN_KENH[k]}</span>
                  </Checkbox>
                  {ch && <Badge variant={ch.variant}>{t(ch.khoa)}</Badge>}
                  {bat && (
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<SendOutlined />}
                      loading={dangThu === k}
                      disabled={dangThu !== null && dangThu !== k}
                      onClick={() => thu(k)}
                    >
                      {dangThu === k ? t('rpaThongBao.form.dangGuiThu') : t('rpaThongBao.form.guiThu')}
                    </Button>
                  )}
                </div>
                {bat && (
                  <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                    <ChonTaiKhoan
                      kenh={k}
                      ds={taiKhoan.data ?? []}
                      value={f.senders[k] ?? ''}
                      onChange={(id) => doi({ senders: { ...f.senders, [k]: id } })}
                    />
                    <Field label={t(`rpaThongBao.kenhDich.${k}`)} required error={loiKenh(k)}>
                      {(p) => (
                        <Input
                          {...p}
                          autoComplete="off"
                          maxLength={GIOI_HAN_QUY_TAC.dich}
                          placeholder={t(`rpaThongBao.kenhMau.${k}`)}
                          value={f.targets[k] ?? ''}
                          onChange={(e) => {
                            doi({ targets: { ...f.targets, [k]: e.target.value } });
                            setLoi((cu) => ({ ...cu, [k]: undefined }));
                          }}
                        />
                      )}
                    </Field>
                  </div>
                )}
              </div>
            );
          })}
        </fieldset>

        <DongBatTat nhan={t('rpaThongBao.form.bat')} bat={f.enabled} onDoi={(enabled) => doi({ enabled })} />
      </form>
    </Dialog>
  );
}

/** Ô chọn tài khoản gửi của một kênh; trống = tài khoản mặc định của loại. Zalo, WhatsApp không có tài khoản gửi. */
function ChonTaiKhoan({
  kenh,
  ds,
  value,
  onChange,
}: {
  kenh: KenhThongBao;
  ds: readonly TaiKhoanGui[];
  value: string;
  onChange: (id: string) => void;
}) {
  const { t } = useT();
  const loai = LOAI_TAI_KHOAN_CUA_KENH[kenh];
  if (!loai) return null;
  const chon = taiKhoanCuaLoai(ds, loai);
  if (chon.length === 0) {
    return <p className="self-center text-xs text-warning-700">{t(`rpaThongBao.khongCoTaiKhoan.${loai}`)}</p>;
  }
  return (
    <Field label={t('rpaThongBao.form.taiKhoan')}>
      {(p) => (
        <Select<string>
          id={p.id}
          className="w-full"
          value={value}
          onChange={onChange}
          options={[
            { value: '', label: t('rpaThongBao.form.taiKhoanMacDinh', { ten: chon[0]?.name ?? '' }) },
            ...chon.map((a) => ({ value: a.id, label: a.name })),
          ]}
        />
      )}
    </Field>
  );
}
